import { useAccessibleModules, useKernel } from "@crialume/core/react";
import { getErrorMessage } from "@crialume/shared";
import { Button, Spinner, StateMessage } from "@crialume/ui";
import { CompassIcon, ShieldWarningIcon, WarningIcon } from "@crialume/ui/icons";
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router";
import { isChunkLoadError, reloadOnce } from "../app/chunk-reload";

export function NotFoundPage() {
  return (
    <StateMessage
      icon={<CompassIcon size={26} />}
      title="Página não encontrada"
      description="O endereço acessado não existe ou foi movido."
      action={
        <Link to="/app" className="text-body-sm text-accent hover:text-accent-2">
          Voltar ao início
        </Link>
      }
    />
  );
}

export function ForbiddenPage() {
  return (
    <StateMessage
      icon={<ShieldWarningIcon size={26} />}
      tone="warning"
      title="Acesso negado"
      description="Você não tem permissão para acessar este módulo."
    />
  );
}

export function ModuleLoading() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-muted-2">
      <Spinner size="lg" />
    </div>
  );
}

export function ModuleFailure({ error, retry }: { error: Error; retry: () => void }) {
  const chunkError = isChunkLoadError(error);
  // Código desatualizado (deploy novo ou reotimização do Vite): repetir o mesmo
  // import falharia de novo — recarrega a página uma vez, automaticamente.
  const [reloading, setReloading] = useState(chunkError);
  useEffect(() => {
    if (chunkError && !reloadOnce()) setReloading(false);
  }, [chunkError]);
  if (reloading) return <ModuleLoading />;

  return (
    <StateMessage
      icon={<WarningIcon size={26} />}
      tone="danger"
      title="Falha ao carregar o módulo"
      description={
        chunkError
          ? "Esta área foi atualizada enquanto a página estava aberta. Recarregue para continuar."
          : getErrorMessage(error, "Erro inesperado.")
      }
      action={
        chunkError ? (
          <Button onClick={() => window.location.reload()}>Recarregar página</Button>
        ) : (
          <Button onClick={retry}>Tentar novamente</Button>
        )
      }
    />
  );
}

/** /app → primeiro módulo acessível. */
export function FirstModuleRedirect() {
  const { modulesBasePath } = useKernel();
  const [first] = useAccessibleModules();

  if (!first) {
    return (
      <StateMessage
        icon={<ShieldWarningIcon size={26} />}
        title="Nenhum módulo disponível"
        description="Seu perfil ainda não tem acesso a nenhuma área do sistema."
      />
    );
  }
  return <Navigate to={`${modulesBasePath}/${first.id}`} replace />;
}
